/**
 * SmartReminderAnnotation
 *
 * Renders a compact reminder confirmation card when the
 * smartReminder tool creates a reminder. Single card, not
 * horizontal scroll. Persists to localStorage.
 */

'use client'

import { useEffect, useRef } from 'react'
import { IconBell, IconCalendar } from '@tabler/icons-react'
import type { Annotation, Annotation2 } from '@/types/annotation'

// --------------

interface ReminderData {
  id: string
  title: string
  dueDate: string
  dueDateFormatted?: string
  notes?: string
  priority: 'low' | 'medium' | 'high'
  createdAt: string
}

// --------------

const STORAGE_KEY = 'losi_reminders'

function persistReminder(reminder: ReminderData) {
  try {
    const existing = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || '[]'
    ) as ReminderData[]
    if (existing.some((r) => r.id === reminder.id)) return
    existing.push(reminder)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing))
  } catch {
    // localStorage unavailable
  }
}

// --------------

function formatDueDate(iso: string): string {
  const date = new Date(iso)
  if (isNaN(date.getTime())) return iso

  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const isToday = date.toDateString() === now.toDateString()
  const isTomorrow = date.toDateString() === tomorrow.toDateString()

  const timeStr = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })

  if (isToday) return `Today at ${timeStr}`
  if (isTomorrow) return `Tomorrow at ${timeStr}`

  const dayDiff = Math.ceil(
    (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  )

  if (dayDiff > 0 && dayDiff <= 7) {
    const dayName = date.toLocaleDateString(undefined, { weekday: 'long' })
    return `${dayName} at ${timeStr}`
  }

  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

// --------------

const priorityConfig = {
  low: {
    label: 'Low',
    bg: 'bg-green-500/15',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-500/30'
  },
  medium: {
    label: 'Medium',
    bg: 'bg-amber-500/15',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/30'
  },
  high: {
    label: 'High',
    bg: 'bg-red-500/15',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-500/30'
  }
}

// --------------

export function SmartReminderAnnotation({
  annotation
}: {
  annotation: Annotation | Annotation2
}) {
  const persisted = useRef(false)

  const raw = (annotation as Record<string, unknown>)[
    'smartReminder__createReminder'
  ]

  const data = raw
    ? typeof raw === 'object' && raw !== null && 'result' in raw
      ? (raw as { result: { reminder?: ReminderData } }).result
      : (raw as { reminder?: ReminderData })
    : null

  const reminder = data?.reminder ?? null

  // Persist to localStorage once
  useEffect(() => {
    if (!reminder || persisted.current) return
    persisted.current = true
    persistReminder(reminder)
  }, [reminder])

  if (!reminder?.title || !reminder?.dueDate) return null

  const priority = priorityConfig[reminder.priority] || priorityConfig.medium
  const displayDate = reminder.dueDateFormatted || formatDueDate(reminder.dueDate)

  return (
    <div className="border-border/40 bg-card/50 max-w-sm rounded-xl border p-3 shadow-sm">
      <div className="flex items-start gap-2.5">
        <div className="bg-primary/10 flex size-7 shrink-0 items-center justify-center rounded-lg">
          <IconBell className="text-primary size-3.5" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-foreground text-sm font-medium leading-tight">
              {reminder.title}
            </span>
            <span
              className={`${priority.bg} ${priority.text} ${priority.border} shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium`}
            >
              {priority.label}
            </span>
          </div>
          <div className="text-muted-foreground flex items-center gap-1 text-xs">
            <IconCalendar className="size-3" />
            <span>{displayDate}</span>
          </div>
          {reminder.notes && (
            <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
              {reminder.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
